import { EmptyCell, Typography } from 'shared/ui'
import { type IColumn } from 'shared/ui/TableNew'
import { numberFormat } from 'shared/lib'
import { type SurveyRow } from 'entities/Surveys/model/Table/SurveyRow'
import { SurveyStatus } from 'entities/Surveys/ui/SurveyStatus/SurveyStatus'
import { SurveyActiveToggle } from 'pages/surveys/ui/SurveyActiveToggle'
import { toggleSurveyActive } from 'pages/surveys/ui/actions/toggleActive/toggleActive'
import styles from '../styles.module.scss'

export const surveysColumns: IColumn<SurveyRow>[] = [
  {
    field: 'name',
    name: 'Name',
    width: '260px',
    isDisabledChangeVisible: true,
    renderRowCell: (row) => SurveyCell(row),
  },
  {
    field: 'status',
    name: 'Status',
    minWidth: '84px',
    maxWidth: '140px',
    isDisabledChangeVisible: true,
    renderRowCell: (row) => <SurveyStatus status={row.status} />,
  },
  {
    field: 'nps_score',
    name: 'NPS score',
    width: '120px',
    tooltipQuestionProps: {
      width: 270,
      placement: 'top',
      breakWord: true,
      label: (
        <Typography variant={'body-md-regular'} ariaLabel={'SurveyNpsScoreTooltip'}>
          NPS (Net Promoter Score) is calculated by subtracting the percentage of detractors (0-6)
          from promoters (9-10), giving a score between -100 and +100
        </Typography>
      ),
    },
    renderRowCell: (row) => (row.npsScore !== null ? row.npsScore : <EmptyCell />),
  },
  {
    field: 'total_sent',
    name: 'Total sent',
    renderRowCell: (row) => (
      <span aria-label={'SurveyTotalSent'}>{numberFormat({ value: row.totalSent })}</span>
    ),
  },
  {
    field: 'valid_replies',
    name: 'Valid replies',
    renderRowCell: (row, _, { isHover }) => (
      <StatisticCell
        isHover={isHover}
        value={row.validReplies}
        percent={row.validRepliesPercent}
        ariaLabel={'SurveyValidReplies'}
      />
    ),
  },
  {
    field: 'undelivered',
    name: 'Undelivered',
    renderRowCell: (row, _, { isHover }) =>
      row.undelivered !== null ? (
        <StatisticCell
          isHover={isHover}
          value={row.undelivered}
          percent={row.undeliveredPercent}
          ariaLabel={'SurveyUndelivered'}
        />
      ) : (
        <EmptyCell />
      ),
  },
  {
    field: 'expired',
    name: 'Expired',
    renderRowCell: (row, _, { isHover }) =>
      row.expired !== null ? (
        <StatisticCell
          isHover={isHover}
          value={row.expired}
          percent={row.expiredPercent}
          ariaLabel={'SurveyExpired'}
        />
      ) : (
        <EmptyCell />
      ),
  },
]

const SurveyCell = (row: SurveyRow) => {
  const { name, status, id } = row
  return (
    <div className={styles.nameCellWrapper}>
      <Typography
        variant={'body-md-regular'}
        ellipsis
        ariaLabel={`SurveyCell_${name}`}
        tooltipProps={{
          label: name,
        }}
      >
        {name}
      </Typography>
      <SurveyActiveToggle item={{ id, status }} onToggle={() => toggleSurveyActive(row.origin)} />
    </div>
  )
}

const StatisticCell = ({
  isHover,
  value,
  percent,
  ariaLabel,
}: {
  isHover: boolean | undefined
  value: number | undefined | null
  percent: number | undefined
  ariaLabel: string
}) => {
  return (
    <span aria-label={ariaLabel}>{isHover ? `${percent}%` : value && numberFormat({ value })}</span>
  )
}
